import { Component, ViewChild } from '@angular/core';
import { filter, forkJoin, map, Observable, switchMap, take } from 'rxjs';
import { SoftwareApiService } from 'src/app/services/api/software-api.service';
import { SimulationWizardDataService } from '../../simulation-wizard-data.service';
import {
  ITableAction,
  ITableData,
  TableComponent,
} from '../../../../gui/table/table.component';
import { DataRequestState } from 'src/app/data-request/model';
import { toRequestState } from 'src/app/data-request/operators';
import { NotificationService } from 'src/app/services/notification.service';
import { Router } from '@angular/router';
import { pagesPATH } from 'src/app/models_new/config/pages';
import { IApiStrategy } from 'src/app/models_new/classes/api-models/ApiStrategy';

type SelectSoftwareActions = 'edit' | 'delete';

@Component({
  selector: 'app-select-software',
  templateUrl: './select-software.component.html',
  styleUrls: ['./select-software.component.scss'],
})
export class SelectSoftwareComponent {
  swPresetsTableData$: Observable<
    DataRequestState<ITableData<SelectSoftwareActions>[]>
  >;
  swTableData$: Observable<
    DataRequestState<ITableData<SelectSoftwareActions>[]>
  >;
  columnsToDisplay: string[] = [
    'select',
    'name',
    'os',
    'pally_software_version',
    'gripper_optimization',
    'max_speed',
    'max_acceleration',
    'approach_distance',
  ];

  presetsExpanded = true;

  @ViewChild('table') table: TableComponent<SelectSoftwareActions>;

  constructor(
    private softwareApi: SoftwareApiService,
    public father: SimulationWizardDataService,
    private notify: NotificationService,
    private router: Router
  ) {
    this.swPresetsTableData$ = this.father.softwarePresets$.pipe(
      map((sw_configs) => {
        let tableData: ITableData<SelectSoftwareActions>[] = [];
        for (let config of sw_configs) {
          tableData.push({
            data: {
              id: config.id,
              name: config.name,
              os: config.data.software.polyscope_version,
              pally_software_version: config.data.software.pally_version,
              gripper_optimization: config.data.gripper_optimization,
              max_speed: config.data.max_speed,
              max_acceleration: config.data.max_acceleration,
              approach_distance: config.data.approach_distance,
              safety_preset: config.data.safety_preset,
            },
          });
        }
        return tableData;
      }),
      toRequestState()
    );

    this.swTableData$ = this.father.softwares$.pipe(
      map((sw_configs) => {
        let tableData: ITableData<SelectSoftwareActions>[] = [];
        for (let config of sw_configs) {
          tableData.push({
            data: {
              id: config.id,
              name: config.name,
              os: config.data.software.polyscope_version,
              pally_software_version: config.data.software.pally_version,
              gripper_optimization: config.data.gripper_optimization,
              max_speed: config.data.max_speed,
              max_acceleration: config.data.max_acceleration,
              approach_distance: config.data.approach_distance,
              safety_preset: config.data.safety_preset,
            },
            actions: [
              {
                label: 'Edit',
                icon: 'edit',
                actionId: 'edit',
              },
              {
                label: 'Delete',
                icon: 'delete',
                actionId: 'delete',
                txtColor: '#AF2B2B',
              },
            ],
          });
        }
        return tableData;
      }),
      toRequestState()
    );
  }

  actionClicked(event: ITableAction<SelectSoftwareActions>) {
    if (event.actionId === 'edit') {
      this.editCallback(event.element);
    } else if (event.actionId === 'delete') {
      this.deleteCallback(event.element);
    }
  }

  createSwConfig() {
    this.router.navigate(
      [pagesPATH.INVENTORY, pagesPATH.SOFTWARE_LIST, 'new'],
      {
        queryParams: {
          origin:
            pagesPATH.SIMULATIONS +
            '/' +
            pagesPATH.SIMULATION_WIZARD +
            '?step=2',
        },
      }
    );
  }

  editCallback(sw_config: IApiStrategy) {
    this.router.navigate(
      [pagesPATH.INVENTORY, pagesPATH.SOFTWARE_LIST, sw_config.id],
      {
        queryParams: {
          origin:
            pagesPATH.SIMULATIONS +
            '/' +
            pagesPATH.SIMULATION_WIZARD +
            '?step=2',
        },
      }
    );
  }

  deleteCallback(sw_config: IApiStrategy) {
    this.notify
      .deletePrompt('Delete', 'Software configuration', [sw_config.name])
      .afterDismissed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => {
          return this.deleteStrategy([sw_config.id]);
        })
      )
      .pipe(take(1))
      .subscribe(() =>
        this.notify.showMessage(
          `The software configuration were deleted successfully!`
        )
      );
  }

  deleteStrategy(ids: string[]): Observable<any> {
    const operations = {};
    for (let i = 0; i < ids.length; i++) {
      operations[i] = this.softwareApi.deleteStrategyById(ids[i]);
    }
    return forkJoin(operations);
  }
}
